import React, { useState, useEffect } from "react";
import { Provider as JotaiProvider, useAtom, useSetAtom } from "jotai";
import { Alert, Badge, Button, DatePicker, Form, Input, Modal, Select, Space, Table, Tag, Upload, message } from "antd";
import {
    CalendarOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    FileExcelOutlined,
    InboxOutlined,
    UploadOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Container } from "../../../components/Container";
import { FiltroProgramarVacaciones } from "./componentes/FiltroProgramarVacaciones";
import { TablaProgramarVacaciones } from "./componentes/TablaProgramarVacaciones";
import { ProgramacionService } from "services/VacacionesService";
import { anioAtom, recargarTablaAtom, selectedRowKeysAtom } from "./ProgramarVacacionesStore";

const anioActual = new Date().getFullYear();
const OPCIONES_ANIO = [{ value: anioActual, label: String(anioActual) }];

/* ── Modal: Importar ────────────────────────────────────────────── */
const ImportarModal = () => {
    const [form] = Form.useForm();
    const [visibleModal, setVisibleModal] = useState(false);
    const [archivo, setArchivo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hayErrores, setHayErrores] = useState(false);
    const [anioFiltro] = useAtom(anioAtom);
    const setRecargar = useSetAtom(recargarTablaAtom);

    const onAbrir = () => {
        setVisibleModal(true);
        setArchivo(null);
        setHayErrores(false);
        form.setFieldsValue({ anio: anioFiltro });
    };

    const onCerrar = () => {
        setVisibleModal(false);
        setHayErrores(false);
    };

    const onConfirmar = async () => {
        const valores = await form.validateFields();
        if (!archivo) {
            message.warning("Seleccione un archivo Excel antes de importar.");
            return;
        }
        setLoading(true);
        setHayErrores(false);
        const formData = new FormData();
        formData.append("anio", String(valores.anio));
        formData.append("excel", archivo);
        const response = await ProgramacionService.importar(formData);
        setLoading(false);

        if (response.esArchivo) {
            const url = URL.createObjectURL(response.blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "errores_programacion_vacaciones.xlsx";
            a.click();
            URL.revokeObjectURL(url);
            setHayErrores(true);
        } else if (response.estado === "EXITO") {
            setVisibleModal(false);
            message.success(`Se importaron ${response.data} registros correctamente.`);
            setRecargar((v) => v + 1);
        }
    };

    return (
        <>
            <Button type="primary" icon={<UploadOutlined />} onClick={onAbrir}>
                Importar Excel
            </Button>
            <Modal
                title="Importar Programación de Vacaciones"
                open={visibleModal}
                onCancel={onCerrar}
                footer={[
                    <Button key="cancelar" onClick={onCerrar}>Cancelar</Button>,
                    <Button key="importar" type="primary" loading={loading} onClick={onConfirmar}>Importar</Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Año de programación"
                        name="anio"
                        rules={[{ required: true, message: "Seleccione el año." }]}
                    >
                        <Select style={{ width: 120 }} options={OPCIONES_ANIO} />
                    </Form.Item>
                </Form>
                <Upload.Dragger
                    name="file"
                    multiple={false}
                    accept=".xlsx,.xls"
                    fileList={archivo ? [archivo] : []}
                    beforeUpload={(file) => { setArchivo(file); return false; }}
                    onRemove={() => setArchivo(null)}
                    maxCount={1}
                >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">Haz clic o arrastra el archivo aquí</p>
                    <p className="ant-upload-hint">
                        Columnas requeridas: <strong>DNI, FECHA INICIO, FECHA FIN</strong>
                        <br />
                        Columna opcional: <strong>FECHA EMISION</strong> (si se llena, reemplaza la fecha de emisión del documento generado)
                    </p>
                </Upload.Dragger>

                {hayErrores && (
                    <Alert
                        style={{ marginTop: 16 }}
                        icon={<WarningOutlined />}
                        message="Se encontraron errores — ningún registro fue insertado"
                        description="Se descargó automáticamente un Excel con el detalle de cada error. Corrija los datos y vuelva a importar."
                        type="warning"
                        showIcon
                    />
                )}
            </Modal>
        </>
    );
};

/* ── Modal: Editar fechas ───────────────────────────────────────── */
const EditarModal = ({ registro, onCerrar }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const setRecargar = useSetAtom(recargarTablaAtom);

    useEffect(() => {
        if (registro) {
            form.setFieldsValue({
                fechaInicio: moment(registro.fechaInicio, "DD/MM/YYYY"),
                fechaFin: moment(registro.fechaFin, "DD/MM/YYYY"),
            });
        }
    }, [registro]);

    const onConfirmar = async () => {
        const valores = await form.validateFields();
        setLoading(true);
        const response = await ProgramacionService.editar(registro.id, {
            fechaInicio: valores.fechaInicio.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
            fechaFin: valores.fechaFin.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        });
        setLoading(false);
        if (response.estado === "EXITO") {
            message.success("Programación actualizada.");
            setRecargar((v) => v + 1);
            onCerrar();
        } else {
            message.error(response.mensaje || "No se pudo actualizar.");
        }
    };

    return (
        <Modal
            title="Editar Programación de Vacaciones"
            open={!!registro}
            onCancel={onCerrar}
            footer={[
                <Button key="cancelar" onClick={onCerrar}>Cancelar</Button>,
                <Button key="guardar" type="primary" loading={loading} onClick={onConfirmar}>Guardar</Button>,
            ]}
        >
            <Form form={form} layout="vertical">
                <Form.Item label="Colaborador">
                    <strong>{registro?.colaboradorNombre}</strong> ({registro?.colaboradorDni})
                </Form.Item>
                <Form.Item
                    label="Fecha Inicio"
                    name="fechaInicio"
                    rules={[{ required: true, message: "Ingrese la fecha de inicio." }]}
                >
                    <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                    label="Fecha Fin"
                    name="fechaFin"
                    rules={[{ required: true, message: "Ingrese la fecha de fin." }]}
                >
                    <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

/* ── Modal: Resultado de aprobación ─────────────────────────────── */
const ResultadoAprobacionModal = ({ visible, resultado, onCerrar }) => {
    if (!resultado) return null;

    const { aprobados, errores = [], excelBase64 } = resultado;

    const descargarExcel = () => {
        const bytes = Uint8Array.from(atob(excelBase64), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "errores_aprobacion_vacaciones.xlsx";
        a.click();
        URL.revokeObjectURL(url);
    };

    const columnas = [
        { title: "DNI", dataIndex: "dni", key: "dni", width: 110 },
        { title: "Colaborador", dataIndex: "nombreColaborador", key: "nombreColaborador" },
        {
            title: "Motivo",
            dataIndex: "motivo",
            key: "motivo",
            render: (text) => <span style={{ color: "#cf1322" }}>{text}</span>,
        },
    ];

    return (
        <Modal
            title="Resultado de aprobación"
            open={visible}
            onCancel={onCerrar}
            footer={[
                excelBase64 && (
                    <Button key="excel" icon={<FileExcelOutlined />} onClick={descargarExcel}>
                        Descargar Excel de errores
                    </Button>
                ),
                <Button key="cerrar" type="primary" onClick={onCerrar}>
                    Cerrar
                </Button>,
            ].filter(Boolean)}
            width={620}
        >
            <Space style={{ marginBottom: errores.length > 0 ? 16 : 0 }}>
                <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 14, padding: "4px 12px" }}>
                    {aprobados} aprobada{aprobados !== 1 ? "s" : ""}
                </Tag>
                {errores.length > 0 && (
                    <Tag color="error" icon={<CloseCircleOutlined />} style={{ fontSize: 14, padding: "4px 12px" }}>
                        {errores.length} con error
                    </Tag>
                )}
            </Space>
            {errores.length > 0 && (
                <Table
                    size="small"
                    dataSource={errores.map((e, i) => ({ ...e, key: i }))}
                    columns={columnas}
                    pagination={false}
                    scroll={{ y: 240 }}
                    bordered
                />
            )}
        </Modal>
    );
};

/* ── Modal: Confirmar clave para aprobar ────────────────────────── */
export const AprobarButton = ({ servicio = ProgramacionService, entidad = "programación(es)" }) => {
    const [selectedRowKeys, setSelectedRowKeys] = useAtom(selectedRowKeysAtom);
    const setRecargar = useSetAtom(recargarTablaAtom);
    const [loading, setLoading] = useState(false);
    const [visibleConfirmar, setVisibleConfirmar] = useState(false);
    const [visibleResultado, setVisibleResultado] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [form] = Form.useForm();

    const onClickAprobar = () => {
        if (selectedRowKeys.length === 0) {
            message.warning("Seleccione al menos un registro pendiente.");
            return;
        }
        form.resetFields();
        setVisibleConfirmar(true);
    };

    const onConfirmar = async () => {
        const valores = await form.validateFields();
        setLoading(true);
        const response = await servicio.aprobar({
            ids: selectedRowKeys,
            clave: valores.clave,
        });
        setLoading(false);

        if (response.estado === "EXITO") {
            const data = response.data || {};
            setResultado(data);
            setVisibleConfirmar(false);
            setVisibleResultado(true);
            if ((data.errores || []).length === 0) {
                setSelectedRowKeys([]);
            }
            setRecargar((v) => v + 1);
        } else {
            message.error(response.mensaje || "No se pudieron aprobar.");
        }
    };

    const onCerrarConfirmar = () => {
        if (loading) return;
        setVisibleConfirmar(false);
    };

    const onCerrarResultado = () => {
        setVisibleResultado(false);
        setSelectedRowKeys([]);
    };

    return (
        <>
            <Button
                icon={<CheckCircleOutlined />}
                onClick={onClickAprobar}
                disabled={selectedRowKeys.length === 0}
            >
                Aprobar seleccionados {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
            </Button>
            <Modal
                title="Confirmar aprobación"
                open={visibleConfirmar}
                onCancel={onCerrarConfirmar}
                footer={[
                    <Button key="cancelar" onClick={onCerrarConfirmar} disabled={loading}>Cancelar</Button>,
                    <Button key="aprobar" type="primary" loading={loading} onClick={onConfirmar}>
                        Aprobar
                    </Button>,
                ]}
            >
                <p>
                    Se aprobarán <strong>{selectedRowKeys.length}</strong> {entidad}.
                    Ingrese su clave para firmar los documentos.
                </p>
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Clave"
                        name="clave"
                        rules={[{ required: true, message: "Ingrese su clave." }]}
                    >
                        <Input.Password placeholder="Ingrese su clave" />
                    </Form.Item>
                </Form>
            </Modal>
            <ResultadoAprobacionModal
                visible={visibleResultado}
                resultado={resultado}
                onCerrar={onCerrarResultado}
            />
        </>
    );
};

/* ── Contenido principal (dentro del Provider) ──────────────────── */
const ProgramarVacacionesContent = () => {
    const [registroEditar, setRegistroEditar] = useState(null);

    return (
        <Container
            icono={<CalendarOutlined />}
            titulo="Programar Vacaciones"
            botonExtra={
                <Space>
                    <AprobarButton />
                    <ImportarModal />
                </Space>
            }
        >
            <FiltroProgramarVacaciones />
            <TablaProgramarVacaciones onEditar={(record) => setRegistroEditar(record)} />
            <EditarModal
                registro={registroEditar}
                onCerrar={() => setRegistroEditar(null)}
            />
        </Container>
    );
};

/* ── Page ───────────────────────────────────────────────────────── */
const ProgramarVacacionesPage = () => {
    return (
        <JotaiProvider>
            <ProgramarVacacionesContent />
        </JotaiProvider>
    );
};

export default ProgramarVacacionesPage;
